<template>
  <div class="image-grid">
    <b-row>
      <b-col
        v-for="image in images"
        :key="image.id"
        cols="12"
        sm="6"
        md="4"
        lg="3"
        class="mb-4"
      >
        <b-card
          no-body
          :class="{ 'border-success': isSelected(image.id) }"
          @click="toggleSelection(image.id)"
          class="h-100 image-card"
        >
          <div class="position-relative">
            <b-img
              :src="image.url"
              :alt="image.name"
              class="image-thumbnail"
              :class="{
                'status-pending': image.status === 'PENDING',
                'status-failed': image.status === 'FAILED',
              }"
              fluid
            ></b-img>

            <div
              class="image-overlay d-flex align-items-center justify-content-center"
              v-if="!image.url"
            >
              <b-spinner variant="primary" label="Đang tải..."></b-spinner>
            </div>

            <div v-if="image.status === 'PENDING'" class="status-badge pending">
              Đang xử lý
            </div>
            <div v-if="image.status === 'FAILED'" class="status-badge failed">
              Thất bại
            </div>

            <b-form-checkbox
              v-if="selectable"
              :checked="isSelected(image.id)"
              class="image-checkbox m-2"
              @change="toggleSelection(image.id)"
              @click.stop
            ></b-form-checkbox>
          </div>

          <b-card-title class="text-truncate p-2 mb-0 h6">
            {{ truncate(image.name, 20) }}
          </b-card-title>

          <b-card-text class="py-1 px-3 small text-muted">
            {{ formatSize(image.size) }} • {{ image.width || '?' }}x{{
              image.height || '?'
            }}
          </b-card-text>

          <b-card-footer class="p-2 bg-white border-top-0">
            <b-button-group class="w-100">
              <b-button
                variant="outline-primary"
                size="sm"
                @click.stop="viewImage(image.id)"
                class="flex-grow-1"
              >
                <i class="fa fa-eye mr-1"></i> Xem
              </b-button>
              <b-button
                variant="outline-danger"
                size="sm"
                @click.stop="confirmDelete(image)"
                :disabled="image.status === 'PENDING'"
                class="flex-grow-1"
              >
                <i class="fa fa-trash mr-1"></i> Xóa
              </b-button>
            </b-button-group>
          </b-card-footer>
        </b-card>
      </b-col>
    </b-row>

    <!-- Empty state message -->
    <b-row v-if="images.length === 0">
      <b-col cols="12" class="text-center my-5">
        <i class="fa fa-image-slash fa-4x text-muted"></i>
        <p class="h5 text-muted mt-3">Không có hình ảnh nào</p>
        <p class="text-muted">Tải lên hình ảnh để bắt đầu</p>
      </b-col>
    </b-row>

    <!-- Delete confirmation dialog -->
    <b-modal
      v-model="deleteDialog"
      title="Xóa Hình ảnh"
      header-bg-variant="light"
      ok-variant="danger"
      ok-title="Xóa"
      cancel-title="Hủy bỏ"
      @ok="handleDeleteImage"
      :ok-disabled="deletingImage"
    >
      <p>Bạn có chắc chắn muốn xóa hình ảnh này không?</p>
      <div
        v-if="imageToDelete && imageToDelete.usageCount > 0"
        class="text-danger mt-2"
      >
        Cảnh báo: Hình ảnh này đang được sử dụng trong
        {{ imageToDelete.usageCount }} vị trí.
      </div>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Hủy bỏ
        </b-button>
        <b-button variant="danger" @click="ok()" :disabled="deletingImage">
          <b-spinner small v-if="deletingImage" class="mr-1"></b-spinner>
          Xóa
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'ImageGrid',
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedIds: this.value || [],
      deleteDialog: false,
      imageToDelete: null,
      deletingImage: false,
    };
  },
  watch: {
    value(newVal) {
      this.selectedIds = newVal || [];
    },
  },
  methods: {
    ...mapActions('images', ['deleteImage', 'getImageUsages']),

    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    toggleSelection(id) {
      if (!this.selectable) return;

      const index = this.selectedIds.indexOf(id);
      if (index === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(index, 1);
      }

      this.$emit('input', this.selectedIds);
      this.$emit('selection-change', this.selectedIds);
    },

    viewImage(id) {
      this.$router.push({ name: 'ImageDetail', params: { id } });
    },

    async confirmDelete(image) {
      try {
        // Check usages before deleting
        const usages = await this.getImageUsages(image.id);

        this.imageToDelete = {
          ...image,
          usageCount: usages ? usages.length : 0,
        };

        this.deleteDialog = true;
      } catch (error) {
        console.error('Error checking image usages:', error);
        this.$toast.error(`Error checking image usages: ${error.message}`);
      }
    },

    async handleDeleteImage() {
      console.log('Attempting to delete image:', this.imageToDelete);
      if (!this.imageToDelete) return;

      this.deletingImage = true;

      try {
        console.log('Calling deleteImage action in Vuex');
        const response = await this.deleteImage(this.imageToDelete.id);

        if (response.success) {
          this.$toast.success('Image deleted successfully');

          // Remove from selection if needed
          const index = this.selectedIds.indexOf(this.imageToDelete.id);
          if (index !== -1) {
            this.selectedIds.splice(index, 1);
            this.$emit('input', this.selectedIds);
            this.$emit('selection-change', this.selectedIds);
          }

          this.deleteDialog = false;
        } else {
          console.error('Failed to delete image:', response.error);
          this.$toast.error(`Failed to delete image: ${response.error}`);
        }
      } catch (error) {
        console.error('Error deleting image:', error);
        this.$toast.error(`Error deleting image: ${error.message}`);
      } finally {
        this.deletingImage = false;
        this.imageToDelete = null;
        console.log('Dialog closed and state reset');
      }
    },

    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substr(0, length) + '...' : text;
    },

    formatSize(bytes) {
      if (!bytes) return '0 B';

      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      while (bytes >= 1024 && i < units.length - 1) {
        bytes /= 1024;
        i++;
      }

      return `${bytes.toFixed(1)} ${units[i]}`;
    },
  },
};
</script>

<style scoped>
.image-grid {
  margin-top: 10px;
}

.image-card {
  transition: all 0.2s ease;
  cursor: pointer;
}

.image-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.image-thumbnail {
  position: relative;
  height: 200px;
  object-fit: cover;
  width: 100%;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
}

.image-checkbox {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 0 0 0 8px;
}

.border-success {
  border: 2px solid #28a745 !important;
}

.status-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

.status-badge.pending {
  background-color: #ffc107;
}

.status-badge.failed {
  background-color: #dc3545;
}

.status-pending {
  opacity: 0.7;
}

.status-failed {
  opacity: 0.5;
  filter: grayscale(50%);
}
</style>
